import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { linkifyText } from './linkify';

// ```lang\ncode``` fenced blocks (the GitHub/Slack/Discord convention) and
// `inline code` spans — the simplest unambiguous way to say "this is code"
// in plain text, no auto-detection heuristics that would misfire on prose.
const FENCE_REGEX = /```([a-zA-Z0-9+#.-]*)\n?([\s\S]*?)```/g;
const INLINE_CODE_REGEX = /`([^`\n]+)`/g;

const CodeBlock = ({ language, code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — nothing to do.
    }
  };

  return (
    <Box
      sx={{
        my: 0.5,
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: '#1e1e2e',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.25,
          py: 0.5,
          bgcolor: 'rgba(255,255,255,0.06)',
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'monospace', textTransform: 'lowercase' }}
        >
          {language || 'code'}
        </Typography>
        <Tooltip title={copied ? 'Copied' : 'Copy code'}>
          <IconButton size="small" onClick={handleCopy} sx={{ color: 'rgba(255,255,255,0.6)', p: 0.25 }}>
            {copied ? (
              <CheckRoundedIcon sx={{ fontSize: 14 }} />
            ) : (
              <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.25,
          overflowX: 'auto',
          fontFamily: '"Fira Code", "JetBrains Mono", Menlo, Consolas, monospace',
          fontSize: 12.5,
          lineHeight: 1.5,
          color: '#e2e2f0',
          whiteSpace: 'pre',
        }}
      >
        <code>{code}</code>
      </Box>
    </Box>
  );
};

/** Inline code spans + links, for text outside any fenced code block. */
const renderInlineSegment = (text, keyPrefix) =>
  text.split(INLINE_CODE_REGEX).map((part, i) =>
    // Odd indices from String.split on a capturing regex are the matches.
    i % 2 === 1 ? (
      <Box
        key={`${keyPrefix}-ic-${i}`}
        component="code"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.88em',
          px: 0.6,
          py: 0.1,
          borderRadius: 0.75,
          bgcolor: 'rgba(0,0,0,0.18)',
        }}
      >
        {part}
      </Box>
    ) : (
      <React.Fragment key={`${keyPrefix}-t-${i}`}>{linkifyText(part)}</React.Fragment>
    ),
  );

/** Renders chat text with ```fenced code blocks```, `inline code`, and clickable links. */
export const formatMessageContent = (text) => {
  if (!text) return text;

  const nodes = [];
  let lastIndex = 0;
  let blockIndex = 0;
  let match;

  FENCE_REGEX.lastIndex = 0;
  while ((match = FENCE_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(
        <React.Fragment key={`t-${blockIndex}`}>
          {renderInlineSegment(text.slice(lastIndex, match.index), `pre-${blockIndex}`)}
        </React.Fragment>,
      );
    }
    nodes.push(
      <CodeBlock key={`code-${blockIndex}`} language={match[1]} code={match[2].replace(/\n$/, '')} />,
    );
    lastIndex = FENCE_REGEX.lastIndex;
    blockIndex += 1;
  }

  if (lastIndex < text.length) {
    nodes.push(
      <React.Fragment key="t-end">{renderInlineSegment(text.slice(lastIndex), 'end')}</React.Fragment>,
    );
  }

  return nodes;
};
